/**
 * Global Loader Service
 * 
 * This service manages a global loading state with request counting.
 * It ensures the loader shows when the first request starts and hides
 * only when all active requests have completed.
 * 
 * Features:
 * - Request counting for parallel API calls
 * - Subscription mechanism for React integration
 * - Timeout protection to prevent infinite loading states
 * - Network status awareness (hides loader when offline)
 */

type LoaderSubscriber = (isLoading: boolean) => void;

class LoaderService {
  private requestCount: number = 0;
  private subscribers: Set<LoaderSubscriber> = new Set();
  private timeoutId: NodeJS.Timeout | null = null;
  private readonly MAX_LOADING_TIME = 30000; // 30 seconds max loading time
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;

  constructor() {
    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  private handleOnline = () => {
    this.isOnline = true;
  };

  private handleOffline = () => {
    this.isOnline = false;
    // When going offline, immediately hide loader and reset count
    if (this.requestCount > 0) {
      this.requestCount = 0;
      this.clearTimeout();
      this.notifySubscribers(false);
    }
  };

  /**
   * Subscribe to loading state changes
   */
  subscribe(callback: LoaderSubscriber): () => void {
    this.subscribers.add(callback);
    // Immediately notify with current state
    callback(this.requestCount > 0);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of state change
   */
  private notifySubscribers(isLoading: boolean): void {
    this.subscribers.forEach(callback => {
      try {
        callback(isLoading);
      } catch (error) {
        console.error('Error in loader subscriber:', error);
      }
    });
  }

  /**
   * Clear the safety timeout
   */
  private clearTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Set a safety timeout to prevent infinite loading
   */
  private setSafetyTimeout(): void {
    this.clearTimeout();
    this.timeoutId = setTimeout(() => {
      if (this.requestCount > 0) {
        console.warn(`Loader safety timeout triggered. Forcing loader hide after ${this.MAX_LOADING_TIME}ms`);
        this.requestCount = 0;
        this.notifySubscribers(false);
      }
    }, this.MAX_LOADING_TIME);
  }

  /**
   * Show loader (increment request count)
   * Called when a GraphQL request starts
   */
  showLoader(): void {
    // Don't show loader if offline
    if (!this.isOnline) {
      return;
    }

    const wasLoading = this.requestCount > 0;
    this.requestCount++;

    // Set safety timeout on first request
    if (this.requestCount === 1) {
      this.setSafetyTimeout();
    }

    // Only notify if we just started loading
    if (!wasLoading) {
      this.notifySubscribers(true);
    }
  }

  /**
   * Hide loader (decrement request count)
   * Called when a GraphQL request completes or errors
   */
  hideLoader(): void {
    // Prevent going negative
    if (this.requestCount > 0) {
      this.requestCount--;
    }

    // Only notify and clear timeout when all requests are done
    if (this.requestCount === 0) {
      this.clearTimeout();
      this.notifySubscribers(false);
    }
  }

  /**
   * Force hide loader (reset count to 0)
   * Use sparingly - only for edge cases like navigation
   */
  forceHide(): void {
    this.requestCount = 0;
    this.clearTimeout();
    this.notifySubscribers(false);
  }

  /**
   * Get current loading state
   */
  isLoading(): boolean {
    return this.requestCount > 0;
  }

  /**
   * Get current request count (for debugging)
   */
  getRequestCount(): number {
    return this.requestCount;
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    this.clearTimeout();
    this.subscribers.clear();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
  }
}

// Export singleton instance
export const loaderService = new LoaderService();

// Export type for external use
export type { LoaderSubscriber };

